<template>
  <!-- NAVBAR COMPONENT Story: CD-2, CD-3 -->
  <div class="flex justify-between items-center p-4 h-[72px]">
    <div class="flex items-center gap-1 cursor-pointer">
      <Button
        variant="ghost"
        size="icon"
        data-testid="navbar_menu_button"
        :aria-label="isSidebarOpen ? 'Close side menu' : 'Open side menu'"
        :title="isSidebarOpen ? 'Close side menu' : 'Open side menu'"
        @click="toggleSidebar"
      >
        <component :is="Menu" class="h-[20px] w-[20px]" />
      </Button>

      <img src="@/assets/logo.svg" :class="isMobile || isTablet ? 'w-[127px] h-[28px]' : 'w-[147px] h-[28px]'" />
    </div>

    <div class="flex items-center gap-1 md:gap-0">
      <TooltipProvider class="hidden md:flex" v-if="isDesktop">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="ghost" size="icon" @click="openHelpCenter">
              <CircleHelp class="text-grey-light-500 w-[20px] h-[20px]" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Frequently Asked Questions</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Popover v-model:open="notificationOpen">
        <PopoverTrigger asChild>
          <Button type="button" variant="ghost" size="icon" aria-label="Notifications" class="relative">
            <Bell class="text-grey-light-500 w-[20px] h-[20px]" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-0.5 right-0.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-semibold px-1"
            >
              {{ unreadCount }}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" :sideOffset="10" class="w-[300px] p-2">
          <div v-if="props.notifications.length === 0" class="p-3 text-sm text-grey-light-500 text-center">
            No notifications
          </div>
          <div v-if="unreadCount > 0" class="flex justify-end px-3 pt-1 pb-2 border-b border-grey-light-200">
            <button class="text-[12px] text-brand-700 hover:underline font-medium" @click="emit('mark-all-read')">
              Mark all as read
            </button>
          </div>
          <div
            v-for="notification in props.notifications"
            :key="notification.id"
            class="flex items-center gap-3 p-3 rounded-md hover:bg-grey-light-50 cursor-pointer"
            @click="goToOrderDetails(notification)"
          >
            <NotificationIcon class="w-5 h-5 text-brand-700 shrink-0" />
            <span class="text-[14px] text-grey-light-700 font-medium">
              {{ capitalize(notification.type) ?? '' }} request: <br />
              {{ upperCase(notification.orderRef ?? notification.orderId) }}
            </span>
          </div>
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger>
          <div class="flex items-center gap-1 ml-2.5">
            <div
              class="flex justify-center items-center w-[40px] h-[40px] border-b border-grey-light-200 rounded-full bg-grey-light-100"
            >
              <span class="text-[16px] font-semibold text-grey-light-500">
                {{ initials }}
              </span>
            </div>
            <ChevronDown class="w-4 h-4 text-gray-700" />
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" :sideOffset="10" class="w-[220px] p-0">
          <DropdownMenuLabel class="py-3 px-4 text-[14px] font-bold text-brand-700 text-center">
            {{ fullName }}
          </DropdownMenuLabel>

          <DropdownMenuSeparator />

          <DropdownMenuItem class="py-3 px-4" @click="router.push('/shopper/profile')">
            <div class="flex items-center gap-2">
              <UserCircle class="w-5 h-5 mr-2" color="#0F7D8B" />
              Profile
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem class="py-3 px-4" @click="emit('sign-out')">
            <div class="flex items-center gap-2">
              <LogOutIcon class="w-5 h-5 mr-2" color="#0F7D8B" />
              Logout
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HELP_CENTER_URL } from '@/config';
import { useUserStore } from '@/stores/users';
import { getInitials } from '@/utils/text.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { capitalize, upperCase } from 'lodash';
import { Bell, ChevronDown, CircleHelp, LogOutIcon, Menu, UserCircle } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import NotificationIcon from '../icons/NotificationIcon.vue';

export interface NavbarNotification {
  id: number;
  change?: string;
  date?: string;
  name?: string;
  reason?: string;
  details?: string;
  orderName?: string;
  orderId?: string;
  orderRef?: string;
  read: boolean;
  state?: string;
  orderRefundRequest?: string;
  type?: string;
  orderType?: string;
}

const props = defineProps<{
  showMobileSidebar: boolean;
  showDesktopSidebar: boolean;
  notifications: NavbarNotification[];
}>();

const emit = defineEmits([
  'open-mobile-sidebar',
  'close-mobile-sidebar',
  'open-desktop-sidebar',
  'close-desktop-sidebar',
  'sign-out',
  'mark-read',
  'mark-all-read',
]);

const { isDesktop, isMobile, isTablet } = useBreakpoint();
const userStore = useUserStore();
const router = useRouter();

const notificationOpen = ref(false);
const unreadCount = computed(() => props.notifications.filter((n) => !n.read).length);

const user = userStore.user;
const fullName = user?.name ?? 'Layup Client';
const initials = user?.name ? getInitials(user.name) : 'LT';

const isSidebarOpen = computed(() => {
  if (isDesktop.value) return props.showDesktopSidebar;
  return props.showMobileSidebar;
});

const toggleSidebar = () => {
  if (isDesktop.value) {
    isSidebarOpen.value ? emit('close-desktop-sidebar') : emit('open-desktop-sidebar');
    emit('close-mobile-sidebar');
  } else {
    isSidebarOpen.value ? emit('close-mobile-sidebar') : emit('open-mobile-sidebar');
    emit('close-desktop-sidebar');
  }
};

const openHelpCenter = () => window.open(HELP_CENTER_URL, '_blank');

const goToOrderDetails = (notification: NavbarNotification) => {
  notificationOpen.value = false;
  if (!notification.read && notification.type !== 'CANCELLATION') {
    emit('mark-read', notification);
  }
  const routeName = notification.orderType === 'REWARDS' ? 'shopperBoostRewardDetails' : 'shopperLaybyDetails';
  router.push({ name: routeName, params: { id: notification.orderId } });
};
</script>
